import { Capacitor } from '@capacitor/core';
import {
    CapacitorPasskey,
    PasskeyPublicKeyCredentialCreationOptionsJSON,
    PasskeyPublicKeyCredentialRequestOptionsJSON,
} from '@capgo/capacitor-passkey';

import { authRequest, AuthenticationPurpose, AuthenticationResult, PasskeyOptions } from './api.ts';

function passkeyOrigin() {
    return Capacitor.isNativePlatform()
        ? new URL(import.meta.env.VITE_BASE_URL).origin
        : window.location.origin;
}

export function isPasskeyCancellation(reason: unknown) {
    if (!reason || typeof reason !== 'object') {
        return false;
    }

    const error = reason as {
        name?: unknown;
        code?: unknown;
        data?: { name?: unknown };
    };

    return (
        error.name === 'AbortError' ||
        error.name === 'NotAllowedError' ||
        error.code === 'AbortError' ||
        error.code === 'NotAllowedError' ||
        error.data?.name === 'AbortError' ||
        error.data?.name === 'NotAllowedError'
    );
}

export function passkeySupport() {
    return CapacitorPasskey.isSupported();
}

export async function authenticateWithPasskey(purpose: AuthenticationPurpose, intended?: string) {
    const ceremony = await authRequest<
        PasskeyOptions<PasskeyPublicKeyCredentialRequestOptionsJSON>
    >(`passkeys/${purpose}/options`, 'GET');
    const credential = await CapacitorPasskey.getCredential({
        mediation: 'required',
        origin: passkeyOrigin(),
        publicKey: ceremony.options,
    });

    return authRequest<AuthenticationResult>(`passkeys/${purpose}`, 'POST', {
        challenge: ceremony.challenge,
        credential,
        intended,
    });
}

export async function authenticateWithPasskeyAutofill(
    purpose: AuthenticationPurpose,
    intended: string | undefined,
    signal: AbortSignal,
    onCredential: () => void,
) {
    const ceremony = await authRequest<
        PasskeyOptions<PasskeyPublicKeyCredentialRequestOptionsJSON>
    >(`passkeys/${purpose}/options`, 'GET');
    const credential = (await navigator.credentials.get({
        mediation: 'conditional',
        publicKey: PublicKeyCredential.parseRequestOptionsFromJSON(
            ceremony.options as PublicKeyCredentialRequestOptionsJSON,
        ),
        signal,
    })) as PublicKeyCredential | null;

    if (!credential) {
        throw new DOMException('Passkey autofill was cancelled.', 'NotAllowedError');
    }

    onCredential();

    return authRequest<AuthenticationResult>(`passkeys/${purpose}`, 'POST', {
        challenge: ceremony.challenge,
        credential: credential.toJSON(),
        intended,
    });
}

export async function registerPasskey() {
    const ceremony = await authRequest<
        PasskeyOptions<PasskeyPublicKeyCredentialCreationOptionsJSON>
    >('passkeys/register/options', 'GET');
    const credential = await CapacitorPasskey.createCredential({
        origin: passkeyOrigin(),
        publicKey: ceremony.options,
    });

    await authRequest('passkeys/register', 'POST', {
        challenge: ceremony.challenge,
        credential,
    });
}
